import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../../context/searchContext";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../../hooks/useListFilters";
import useI18n from "../../../../../../hooks/useTranslations";
import { AdminWallMaterial } from "../../../../../../http/vertigrip/types/wallMaterials";
import { useWallMaterialsApi } from "../../../../../../http/vertigrip/useWallMaterials";
import { PaginationQuery } from "../../../../../../types";
import FlexContent from "../../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../../ui/ListItemMenu";
import ModalDialog from "../../../../../ui/ModalDialog";
import SearchBar from "../../../../../ui/SearchBar";
import Table from "../../../../../ui/Table";
import Visibility from "../../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";
import CreateWallMaterialModalDialogButton from "./CreateWallMaterialModalDialogButton";

type Props = {
  active?: boolean;
};

const WallMaterials: FC<Props> = ({ active }) => {
  const navigate = useNavigate();
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { installationTypeId } = useParams<{ installationTypeId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "wallMaterials" });
  const aborter = useRef(new AbortController());
  const wallMaterialApi = useWallMaterialsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "wallMaterials");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await wallMaterialApi.updateAdminWallMaterialVisibility(id, { visibility }, { installationTypeId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (wallMaterialId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await wallMaterialApi.updateAdminWallMaterialOrder(wallMaterialId, { order: newOrder }, { installationTypeId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToDelete, setItemToDelete] = useState<AdminWallMaterial>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminWallMaterial>("vertigrip.wall_material");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = wallMaterialApi.useAdminInstallationTypeWallMaterials(
    installationTypeId!,
    query,
    aborter.current.signal,
  );

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.wallMaterials.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.wallMaterials.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateWallMaterialModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminWallMaterial>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${item.id}`}
              >
                {item.name}
              </Link>
            </td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) =>
          navigate(`/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${item.id}`)
        }
        rowActions={(item) => [
          getEditAction(i18n, () =>
            navigate(`/admin/systems/${systemId}/installation_types/${installationTypeId}/wall_materials/${item.id}`),
          ),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.wallMaterials.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                wallMaterialApi
                  .deleteAdminWallMaterial(itemToDelete.id, itemToDelete.installationType.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.wallMaterials.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default WallMaterials;
